import React from 'react';

const AdvancedPagination = ({
  currentPage,
  totalPages,
  totalCount,
  limitPerPage,
  onPageChange,
  onLimitChange,
  pageSizeOptions = [10, 20, 25, 50, 100]
}) => {
  if (totalPages <= 1) return null;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      onPageChange(newPage);
    }
  };

  const handleLimitChange = (newLimit) => {
    onLimitChange(parseInt(newLimit));
  };

  const handleDirectPageInput = (value) => {
    const pageNum = parseInt(value);
    if (pageNum >= 1 && pageNum <= totalPages) {
      handlePageChange(pageNum);
    }
  };

  const renderPageButtons = () => {
    const buttons = [];

    // Always show first page
    if (currentPage > 3) {
      buttons.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="relative inline-flex items-center px-4 py-2 border bg-white border-gray-300 text-sm font-medium text-gray-500 hover:bg-gray-50"
        >
          1
        </button>
      );

      if (currentPage > 4) {
        buttons.push(
          <span key="ellipsis1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
            ...
          </span>
        );
      }
    }

    // Show pages around current page
    for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
            i === currentPage
              ? 'z-10 bg-blue-600 border-blue-600 text-white'
              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
          }`}
        >
          {i.toLocaleString('tr-TR')}
        </button>
      );
    }

    // Always show last page
    if (currentPage < totalPages - 2) {
      if (currentPage < totalPages - 3) {
        buttons.push(
          <span key="ellipsis2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
            ...
          </span>
        );
      }

      buttons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="relative inline-flex items-center px-4 py-2 border bg-white border-gray-300 text-sm font-medium text-gray-500 hover:bg-gray-50"
        >
          {totalPages.toLocaleString('tr-TR')}
        </button>
      );
    }

    return buttons;
  };

  return (
    <div className="bg-white px-4 py-3 flex flex-col gap-4 border-t border-gray-200 sm:px-6">
      {/* Mobile View */}
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          Önceki
        </button>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          Sonraki
        </button>
      </div>

      {/* Desktop View */}
      <div className="hidden sm:flex sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <p className="text-sm text-gray-700">
            <span className="font-medium">{((currentPage - 1) * limitPerPage) + 1}</span>
            {' '}-{' '}
            <span className="font-medium">
              {Math.min(currentPage * limitPerPage, totalCount)}
            </span>
            {' '}arası, toplam{' '}
            <span className="font-medium">{totalCount.toLocaleString('tr-TR')}</span> sonuç
          </p>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700">Sayfa başına:</label>
            <select
              value={limitPerPage}
              onChange={(e) => handleLimitChange(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              {pageSizeOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700">Sayfa:</label>
            <input
              type="number"
              min="1"
              max={totalPages}
              value={currentPage}
              onChange={(e) => handleDirectPageInput(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-20"
            />
            <span className="text-sm text-gray-700">/ {totalPages.toLocaleString('tr-TR')}</span>
          </div>
          <nav className="relative z-0 inline-flex items-center rounded-md shadow-sm -space-x-px">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              İlk
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              Önceki
            </button>

            {renderPageButtons()}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              Sonraki
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              Son
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default AdvancedPagination;